import { Component, input, inject, signal, computed, viewChild, ElementRef, effect } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Auth } from '@angular/fire/auth';
import { Order, OrderComment } from '../order.model';
import { OrdersService } from '../orders.service';
import { CommentBubbleComponent } from './comment-bubble/comment-bubble.component';

@Component({
  selector: 'app-order-comments',
  standalone: true,
  imports: [FormsModule, CommentBubbleComponent],
  templateUrl: './order-comments.component.html',
  styleUrl: './order-comments.component.css'
})
export class OrderCommentsComponent {
  private readonly ordersService = inject(OrdersService);
  private readonly auth = inject(Auth);
  
  order = input.required<Order>();
  commentsContainer = viewChild<ElementRef>('commentsContainer');
  
  newComment = signal('');
  isSubmitting = signal(false);

  comments = computed(() => this.order().comments ?? []);
  currentUserId = computed(() => this.auth.currentUser?.uid ?? '');

  constructor() {
    // Scroll to bottom when comments change or component initializes
    effect(() => {
      this.comments(); // Track changes
      this.scrollToBottom();
    });
  }

  isCurrentUserComment(comment: OrderComment): boolean {
    return comment.userId === this.currentUserId();
  }

  async sendComment(): Promise<void> {
    const message = this.newComment().trim();
    if (!message || this.isSubmitting()) return;

    this.isSubmitting.set(true);
    try {
      await this.ordersService.addComment(this.order().id, message);
      this.newComment.set('');
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Failed to add comment. Please try again.');
    } finally {
      this.isSubmitting.set(false);
    }
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      const container = this.commentsContainer()?.nativeElement;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    }, 0);
  }
}
